import { autoinject, bindable, Container } from 'aurelia-framework';
import { ListField } from 'models/ListSettings';
import { CommentForEntity, MooringArticleSearchResult, MooringCage, MooringComponent } from 'models/mooring';
import { MooringArticleService, ToastService } from 'services';
import { MooringComponentSearchHandler, MooringOfferService, MooringService } from 'services/mooring';
import { toM } from 'lib/ui/ValueDisplayFormatters';
import { MooringTableBase } from '../table-base';
import { TableEntryManager } from 'services/ui-helpers';
import { DialogService } from 'aurelia-dialog';
import { PubSub } from 'lib/event/PubSub';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';
import { AsyncErrorHandler } from 'lib/ui';
import { TableManager } from '../TableManager';
import { SearchResult } from 'elements/search/search-input';
import { FloaterTypeService } from 'services/floater-type-service';
import { Models } from 'models/core';
import { I18N } from 'aurelia-i18n';
import { MooringSelectEntriesForPupulateArgs } from 'features/mooring/component-templates/mooring-select-entries-for-populate';
import { MaterialFactor } from 'models/mooring/MaterialFactor';
import { MaterialFactorService } from 'services/material-factor-service';
import { OfferViewContext } from '../../OfferViewContext';

@autoinject
export class CagesTable extends MooringTableBase<MooringCage> {
  protected table = new TableManager('cages-table', tableColumns);

  @bindable
  protected onSave: (args: { entries: MooringCage[]; canNavigateOnSave: boolean }) => void;

  protected floaters: Models.FloaterType[];
  protected context = OfferViewContext.useContext(this.container);

  constructor(
    private container: Container,
    protected toast: ToastService,
    private foaterTypeService: FloaterTypeService,
    private t: I18N,
    dialogService: DialogService,
    mooringArticleService: MooringArticleService,
    // Used in HTML for article search
    mooringComponentSearchHandler: MooringComponentSearchHandler,
    tableEntryManager: TableEntryManager,
    pubsub: PubSub,
    confirmation: ConfirmDialog,
    mooringOfferService: MooringOfferService,
    mooringService: MooringService,
    materialFactorService: MaterialFactorService,
    materialFactors: MaterialFactor
  ) {
    super(
      'cage',
      dialogService,
      mooringComponentSearchHandler,
      tableEntryManager,
      mooringArticleService,
      pubsub,
      confirmation,
      mooringOfferService,
      mooringService,
      materialFactorService,
      materialFactors
    );

    pubsub.sub('tabs:change-guard', async () => {
      if (this.changeTracker.areEqual(this.entries)) {
        this.pubsub.publish('tabs:change-guard-ok', null);
        return;
      }

      const res = await this.confirmation.confirmSave();

      if (res == 'cancel') {
        return;
      } else if (res === 'discard-continue') {
        this.pubsub.publish('tabs:change-guard-ok', null);
      } else {
        this.onSave({ entries: this.entries, canNavigateOnSave: true });
      }
    });
  }

  async attached() {
    await this.getFloaters();

    await super.attached();
  }

  @AsyncErrorHandler
  public async searchForFloater(term: string) {
    if (!this.floaters || !this.floaters.length) return [];

    return this.floaters
      .filter((x) => x.Name.toLowerCase().includes(term.toLowerCase()) && !x.IsDeleted)
      .map((x) => ({
        id: x.Id,
        title: `${x.Name}`,
        meta: x.NoBrackets ? undefined : `(${x.NoBrackets} ${this.t.tr('mooring.noBrackets')})`,
        extra: [
          `${x.Producer}`,
          // `${this.t.tr('general.producer')}: ${x.SupplierName?.slice(0, 30)}`,
        ],
        return: x,
      }));
  }

  public onFloaterSelected(selection: { id: number; title: string }, item: MooringCage) {
    item.FloaterId = selection.id;
  }

  @AsyncErrorHandler
  public async getFloaterDetails(entry?: { item: Models.FloaterType; id: number }) {
    if (!entry) return;
    const { id } = entry;

    const floater = this.floaters.find((x) => x.Id == id);
    if (!floater) return undefined;

    return {
      id: floater.Id,
      title: floater.Name,
    };
  }

  protected async getFloaters() {
    this.floaters = await this.foaterTypeService.getAllCached();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected postPasteAdjustment(_: number, __: string): void {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected setComponentQuantity(_: MooringComponent, __: MooringCage) {}

  // This method is not used in this table, but added for consistency with other tables.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onInputChange(_: number, __: (typeof tableColumns)[number]['field']) {}

  protected async onArticleSelected(searchResult: SearchResult, component: MooringComponent) {
    const res = searchResult.return as MooringArticleSearchResult;
    component.ArticleProductId = res.ArticleProductId;
    component.MBL = res.MBL;
    component.CategoryName = res.Category;
    component.TypeName = res.Type;
    component.ProductTypeName = res.ProductType;

    this.onSave({
      entries: this.entries,
      canNavigateOnSave: false,
    });
  }

  protected getEntriesForPopulate(): MooringSelectEntriesForPupulateArgs {
    const entries = structuredClone(this.entries);
    return {
      groups: [
        {
          _identifier: 'cages',
          title: this.t.tr('mooring.floater_plural'),
          options: entries
            .sort((a, b) => a.AnalysisNo.localeCompare(b.AnalysisNo))
            .map((x) => ({ name: x.AnalysisNo || '', id: x.Id, selected: false, articles: x.Components?.length || 0 })),
        },
      ],
    };
  }

  protected onAddComponent() {
    this.entries.push({
      AnalysisNo: `-`,
    } as MooringCage);
  }

  @AsyncErrorHandler
  protected async onDelete(index: number) {
    const res = await this.confirmation.confirmDelete();
    if (!res) return;

    const entry = this.entries[index];
    if (!entry) return;

    if (entry.Id) {
      await this.mooringService.deleteCage(this.mooringId, entry.Id);
    }

    this.entries.splice(index, 1);
    this.toast.showDeleted();
  }

  protected save() {
    this.onSave({
      entries: this.entries,
      canNavigateOnSave: false,
    });
  }

  protected addEntryComment(component: MooringCage) {
    this.openCommentDialog({
      mooringId: this.mooringId,
      isReadOnly: false,
      componentId: component.Id,
      componentType: CommentForEntity.Cage,
    });
  }

  protected unbind() {
    this.pubsub.unsub();
    super.unbind();
  }

  protected detached() {
    this.pubsub.unsub();
  }
}

const tableColumns: ListField[] = [
  {
    field: 'AnalysisNo',
    title: 'mooring.analysisNo',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: (value: string) => value,
    inputType: 'text',
  },
  {
    field: 'FloaterId',
    title: 'mooring.floaterType',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    inputType: 'custom',
  },
  // {
  //   field: 'Row',
  //   title: 'general.row',
  //   selected: true,
  //   visible: true,
  //   filterType: 'TEXT',
  //   inputType: 'int',
  // },
  // {
  //   field: 'Column',
  //   title: 'general.column',
  //   selected: true,
  //   visible: true,
  //   filterType: 'TEXT',
  //   inputType: 'int',
  // },
  {
    field: 'actions',
    title: '',
    selected: true,
    visible: true,
    filterType: 'none',
  },
];
